import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/app/AppShell";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";

export default function ProgressPage() {
  const { t, tDomain } = useT();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/attempts");
        setAttempts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const finished = attempts.filter((a) => a.finished_at).reverse();
  const lineData = finished.map((a) => ({
    date: a.finished_at?.slice(5, 10),
    score: a.score_pct,
    readiness: a.readiness_score,
  }));

  const recent = finished.slice(-5);
  const domains = ["People", "Process", "Business Environment"];
  const barData = domains.map((d) => {
    const vals = recent.map((a) => (a.domain_scores || {})[d]).filter((v) => typeof v === "number");
    const avg = vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : 0;
    return { domain: tDomain(d), average: avg };
  });

  return (
    <AppShell>
      <div className="overline text-[#002FA7]">{t("progress.overline")}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("progress.title")}</h1>

      {loading ? (
        <div className="h-40 grid place-items-center" data-testid="progress-loading">
          <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" />
        </div>
      ) : finished.length === 0 ? (
        <div className="card mt-6 text-center">
          <h3 className="font-display text-xl font-semibold">{t("progress.no_attempts")}</h3>
          <p className="text-slate-600 mt-2">{t("progress.no_attempts_sub")}</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="overline">{t("progress.score_readiness")}</div>
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, color: "#fff" }} />
                  <Legend />
                  <Line type="monotone" dataKey="score" stroke="#002FA7" strokeWidth={2} />
                  <Line type="monotone" dataKey="readiness" stroke="#E11D48" strokeWidth={2} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <div className="overline">{t("progress.domain_avg")}</div>
            <div className="h-64 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="domain" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, color: "#fff" }} />
                  <Bar dataKey="average" fill="#002FA7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card lg:col-span-2">
            <div className="overline">{t("progress.history")}</div>
            <table className="w-full mt-4 text-sm" data-testid="progress-attempts-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="py-2">{t("progress.col.date")}</th>
                  <th>{t("progress.col.mode")}</th>
                  <th>{t("progress.col.score")}</th>
                  <th>{t("progress.col.readiness")}</th>
                  <th>{t("progress.col.questions")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...finished].reverse().map((a) => (
                  <tr key={a.attempt_id} className="border-t border-slate-100">
                    <td className="py-3">{a.finished_at?.slice(0, 10)}</td>
                    <td className="capitalize">{a.mode}</td>
                    <td>{a.score_pct}%</td>
                    <td className="font-mono font-bold">{a.readiness_score}</td>
                    <td>{a.questions.length}</td>
                    <td><Link className="text-[#002FA7] font-semibold hover:underline" to={`/results/${a.attempt_id}`} data-testid={`progress-view-${a.attempt_id}`}>{t("progress.review_link")}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppShell>
  );
}
